import { CITATION_LIST_FIELD, type CitationGroup, type DocumentCitationState, DocumentCitationStyle, DocumentCitationStyleLabels, generateObjectId, type SlateContents } from '@alpod-ai-writer/shared';
import { NodeApi, type Value } from '@udecode/plate';
import { MarkdownPlugin } from '@udecode/plate-markdown';
import clsx from 'clsx';
import { Avatar, AvatarGroup, Button, Label, TextInput } from 'flowbite-react';
import { AlertTriangleIcon, LifeBuoyIcon, PencilIcon, SparklesIcon, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Y from 'yjs';

import { useCredits } from '../../app/hooks/useCredits.hook';
import { AiFeatureType } from '../../app/types/credit.types';
import { CreditConfirmationModal } from '../../components/modals/creditConfirmation/creditConfirmationModal.component';
import { InsufficientCreditsModal } from '../../components/modals/insufficientCredits/insufficientCreditsModal.component';
import { useModals } from '../../hooks/useModals.hook';
import { getInitialsFromName } from '../../lib/identity.utils';
import { useAiDisclaimer } from '../hooks/useAiDisclaimer.hook';
import { type CitationRef,useCitationRefs } from '../hooks/useCitationRefs';
import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { useInlineEditor } from '../hooks/useInlineEditor.hook';
import { useInlineEditorStatic } from '../hooks/useInlineEditorStatic.hook';
import { getSectionLink, useSectionNav } from '../hooks/useSectionNav.hook';
import { useYDocAwarenessFieldSelection } from '../hooks/useYDocAwernessSelection.hook';
import { useYDocMap } from '../hooks/useYDocText.hook';
import { limitString } from '../utils/string.utils';
import { AiCitationModal } from './common/aiCitationModal.component';
import { CitationImportModal } from './common/citationImportModal.component';
import { InlineEditor } from './common/inlineEditor.component';
import { InlineEditorReadonly } from './common/inlineEditorReadonly.component';

const getUserLabelPlaceholder = (citationStyle: DocumentCitationStyle): string => {
    switch (citationStyle) {
        case DocumentCitationStyle.APA7:
            return 'napr. Novák, 2025';
        case DocumentCitationStyle.ISO690_2011_Harvard:
        case DocumentCitationStyle.ISO690_2022_Harvard:
            return 'napr. NOVÁK, 2025';
        case DocumentCitationStyle.ISO690_2011_Vancouver:
        case DocumentCitationStyle.ISO690_2022_Vancouver:
            return 'napr. [1]';
        case DocumentCitationStyle.ISO690_2011_Footnote:
        case DocumentCitationStyle.ISO690_2022_Footnote:
            return 'napr. 1';
        default:
            return 'napr. Novák, 2025';
    }
};

const createSlateContents = (text: string): SlateContents => ({
    children: [
        { type: 'p', children: [ { text } ] }
    ]
});

export interface CitationListEditorProps {
    yDoc: Y.Doc;
    readOnly: boolean;
    citationGroups: CitationGroup[];
}

export const CitationListEditor: React.FC<CitationListEditorProps> = ({
    yDoc,
    readOnly,
    citationGroups,
}) => {
    const { entries, addEntry, updateEntry, removeEntry } = useYDocMap<DocumentCitationState>(yDoc, CITATION_LIST_FIELD);
    const { citationRefs } = useCitationRefs();
    const modals = useModals();

    const handleAddEntry = useCallback((groupId?: string) => {
        const id = generateObjectId();
        addEntry(id, {
            userLabel: '',
            source: '',
            citation: null,
            content: createSlateContents(''),
            groupId,
        });
    }, [ addEntry ]);

    const handleUpdateEntry = useCallback((key: string, entry: DocumentCitationState) => {
        updateEntry(key, entry);
    }, [ updateEntry ]);

    const handleRemoveEntry = useCallback((key: string) => {
        removeEntry(key);
    }, [ removeEntry ]);

    const handleImport = useCallback(() => {
        modals.showModal('citation-import', CitationImportModal, {
            onImport: (data: string[]) => {
                data.forEach((item) => {
                    const id = generateObjectId();
                    addEntry(id, {
                        userLabel: item,
                        source: '',
                        citation: null,
                        content: createSlateContents(item)
                    });
                });
            }
        });
    }, [ addEntry, modals ]);

    const hasGroups = citationGroups.length > 0;

    const renderEntry = (key: string, entry: DocumentCitationState) => (
        <CitationListEntry
            key={key}
            entryKey={key}
            entry={entry}
            refs={citationRefs.get(key) ?? []}
            readOnly={readOnly}
            citationGroups={citationGroups}
            onUpdate={handleUpdateEntry}
            onRemove={handleRemoveEntry}
        />
    );

    if (hasGroups) {
        const allIds = new Set<string>();
        for (const group of citationGroups) {
            allIds.add(group.id);
            for (const sub of group.subgroups ?? []) allIds.add(sub.id);
        }

        const grouped = new Map<string | undefined, Array<[string, DocumentCitationState]>>();
        for (const id of allIds) grouped.set(id, []);
        grouped.set(undefined, []);

        for (const [key, entry] of entries) {
            const gid = entry.groupId && allIds.has(entry.groupId) ? entry.groupId : undefined;
            grouped.get(gid)!.push([ key, entry ]);
        }

        const renderGroupEntries = (id: string, addLabel: string) => {
            const groupEntries = grouped.get(id) ?? [];
            return (
                <>
                    <div className='flex flex-col gap-2'>
                        {groupEntries.map(([ key, entry ]) => renderEntry(key, entry))}
                        {!groupEntries.length && (
                            <div className='text-gray-400 text-xs italic px-1'>Žádné citace.</div>
                        )}
                    </div>
                    {!readOnly && (
                        <div className='flex justify-end'>
                            <Button size='xs' color='primary' onClick={() => handleAddEntry(id)}>{addLabel}</Button>
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className='flex flex-col gap-8 px-1 md:px-0'>
                {citationGroups.map((group) => {
                    const hasSubgroups = (group.subgroups ?? []).length > 0;
                    return (
                        <div key={group.id} className='flex flex-col gap-3'>
                            <h3 className='text-sm font-semibold text-gray-700 border-b pb-1'>{group.label}</h3>
                            {hasSubgroups ? (
                                <div className='flex flex-col gap-5'>
                                    {(group.subgroups ?? []).map((sub) => (
                                        <div key={sub.id} className='flex flex-col gap-2 pl-3 border-l-2 border-gray-200'>
                                            <h4 className='text-xs font-semibold text-gray-600'>{sub.label}</h4>
                                            {renderGroupEntries(sub.id, 'Přidat citaci')}
                                        </div>
                                    ))}
                                    {(grouped.get(group.id) ?? []).length > 0 && (
                                        <div className='flex flex-col gap-2'>
                                            <h4 className='text-xs font-semibold text-gray-500 italic'>Ostatné</h4>
                                            {renderGroupEntries(group.id, 'Přidat citaci')}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                renderGroupEntries(group.id, 'Přidat citaci')
                            )}
                        </div>
                    );
                })}

                {(grouped.get(undefined) ?? []).length > 0 && (
                    <div className='flex flex-col gap-3'>
                        <h3 className='text-sm font-semibold text-gray-500 border-b pb-1'>Bez kategorie</h3>
                        <div className='flex flex-col gap-2'>
                            {(grouped.get(undefined) ?? []).map(([ key, entry ]) => renderEntry(key, entry))}
                        </div>
                    </div>
                )}

                {!readOnly && (
                    <div className='flex flex-row items-center justify-end gap-2 px-4 md:px-0'>
                        <Button onClick={handleImport} color='primary'>Vložit hromadně</Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-6 px-1 md:px-0'>
            <div className='flex flex-col gap-2'>
                {entries.map(([ key, entry ]) => renderEntry(key, entry))}
                {!entries.length && (
                    <div className='text-gray-500 text-left border border-gray-300 text-sm p-4 rounded-lg'>Do dokumentu zatím nebyly vloženy žádné citace. Přidejte citaci níže nebo ji rovnou vložte do textu napsáním znaku <code className='bg-gray-200 rounded p-0.5'>@</code>.</div>
                )}
            </div>
            {!readOnly && (
                <div className='flex flex-row items-center justify-end gap-2 px-4 md:px-0'>
                    <Button onClick={handleImport} color='primary'>Vložit hromadně</Button>
                    <Button onClick={() => handleAddEntry()} color='primary'>Přidat citaci</Button>
                </div>
            )}
        </div>
    );
};

const CitationListEntry: React.FC<{
    entryKey: string;
    entry: DocumentCitationState;
    refs: CitationRef[];
    readOnly: boolean;
    citationGroups: CitationGroup[];
    onUpdate: (key: string, entry: DocumentCitationState) => void;
    onRemove: (key: string) => void;
}> = ({
    entryKey,
    entry,
    refs,
    readOnly,
    citationGroups,
    onUpdate,
    onRemove
}) => {
    const docEditor = useDocumentEditor();
    const { currentSection } = useSectionNav(docEditor.template.config.sections);

    const {
        selectedBy,
        resetAwareness: resetAwerness,
        setAwarenessToField: setAwernessToField
    } = useYDocAwarenessFieldSelection(docEditor.yjsProvider, `${CITATION_LIST_FIELD}.${entryKey}`);

    const normalizedContent = (entry.content ?? createSlateContents(''));
    const [ isOpened, setIsOpened ] = useState(currentSection?.headingId === entryKey);

    const toggleOpen = useCallback((ev: React.MouseEvent) => {
        ev.stopPropagation();

        setIsOpened((prev) => !prev);
    }, []);

    const handleRemove = useCallback(() => {
        onRemove(entryKey);
    }, [ onRemove, entryKey ]);

    const handleFocus = useCallback(() => {
        setAwernessToField();
    }, [ setAwernessToField, entryKey, isOpened ]);

    const handleBlur = useCallback(() => {
        resetAwerness();
    }, [ resetAwerness, entryKey ]);

    const editor = useInlineEditorStatic({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        value: normalizedContent.children as Value,
    });

    useEffect(() => {
        editor.tf.setValue(normalizedContent.children as Value);
    }, [ editor, entry.content ]);

    useEffect(() => {
        if (currentSection?.headingId === entryKey) {
            // Scroll to the entry when it's focused
            const targetEl = document.getElementById(`${currentSection.sectionId}/${entryKey}`);
            if (targetEl) {
                targetEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }
    }, []);

    useEffect(() => {
        return () => {
            // Reset awareness when unmounting to handle case when user didn't close the entry
            resetAwerness();
        };
    }, []);

    const selectionColor = selectedBy[0]?.color ?? undefined;
    const hasCitationContents = NodeApi.isNode(normalizedContent) && NodeApi.string(normalizedContent).length > 0;

    return (
        <div className='relative'>
            <div
                className={clsx(
                    'w-full flex flex-col rounded-xl overflow-hidden shadow-[0px_0px_10px_transparent] border-2 transition-all duration-200',
                    isOpened && 'focus-within:shadow-primary-200 border-primary-400',
                    !isOpened && 'border-transparent'
                )}
                style={{
                    borderColor: selectionColor,
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                <div
                    className={clsx(
                        'flex flex-row items-start justify-start pl-5 pr-4 py-4 gap-1 cursor-pointer transition-colors duration-200 bg-white hover:bg-gray-200 min-w-0',
                    )}
                    onClick={toggleOpen}
                >
                    <a id={`${currentSection?.sectionId}/${entryKey}`} />
                    <button
                        className='flex flex-col grow items-start justify-start text-left gap-1 min-w-0 [overflow-wrap:anywhere]'
                        onClick={toggleOpen}
                    >
                        <span className='block text-sm font-semibold'>{entry.userLabel || '(prázdná položka)'}</span>
                        {hasCitationContents && (
                            <InlineEditorReadonly
                                editor={editor}
                                className='text-sm [&_.slate-editor]:![overflow-wrap:anywhere]'
                            />
                        )}
                        {!hasCitationContents && (
                            <span className='flex flex-row items-center gap-1 text-sm text-red-800 font-semibold'>
                                <AlertTriangleIcon size={16} /> <span>Znění citace není vyplněno.</span>
                            </span>
                        )}
                        <span className='flex flex-row flex-wrap gap-2 pt-1'>
                            {refs.length > 0 && (
                                <>
                                    {refs.map((ref, index) => (
                                        <a
                                            key={index}
                                            href={getSectionLink(ref.sectionId, ref.headingId)}
                                            className='text-xs text-primary-500 underline hover:no-underline'
                                            onClick={(ev) => ev.stopPropagation()}
                                        >{ref.sectionTitle}{ref.headingTitle ? ' - ' + limitString(ref.headingTitle, 20) : ''}</a>
                                    ))}
                                </>
                            )}
                            {refs.length === 0 && (
                                <span className='flex flex-row items-center gap-1 text-xs text-gray-600'><AlertTriangleIcon size={14} /> Není použito nikde v dokumentu.</span>
                            )}
                        </span>
                    </button>
                    {!readOnly && (
                        <>
                            <button
                                className={clsx(
                                    'bg-transparent hover:bg-white hover:text-primary-500 rounded-md transition-colors duration-200 p-2',
                                    isOpened && 'text-primary-550'
                                )}
                                onClick={toggleOpen}
                            ><PencilIcon size={20} /></button>
                            <button
                                className='bg-transparent hover:bg-white hover:text-red-800 rounded-md transition-colors duration-200 p-2'
                                onClick={handleRemove}
                            ><TrashIcon size={20} /></button>
                        </>
                    )}
                </div>
                {isOpened && (
                    <CitationListEntryEdit
                        entry={entry}
                        entryKey={entryKey}
                        readOnly={readOnly}
                        citationGroups={citationGroups}
                        onUpdate={onUpdate}
                    />
                )}
            </div>
            <AvatarGroup className='absolute right-2 bottom-0 translate-y-1/2 z-10'>
                {selectedBy?.map((cursor) => (
                    <Avatar
                        key={cursor.clientId}
                        alt={cursor.name}
                        title={cursor.name}
                        placeholderInitials={getInitialsFromName(cursor.name)}
                        size='xs'
                        rounded
                        style={{
                            backgroundColor: cursor.color,
                            borderRadius: '1000px'
                        }}
                        theme={{
                            root: {
                                base: 'relative',
                                initials: {
                                    base: 'flex flex-row items-center justify-center bg-inherit',
                                    text: 'text-white text-xs'
                                }
                            },
                        }}
                    />
                ))}
            </AvatarGroup>
        </div>
    );
};

const CitationListEntryEdit: React.FC<{
    entryKey: string;
    entry: DocumentCitationState;
    readOnly: boolean;
    citationGroups: CitationGroup[];
    onUpdate: (key: string, entry: DocumentCitationState) => void;
}> = ({
    entryKey,
    entry,
    readOnly,
    citationGroups,
    onUpdate,
}) => {
    const docEditor = useDocumentEditor();
    const modals = useModals();
    const { useAiFeature } = useAiDisclaimer();
    const { canUseFeature } = useCredits();

    const { register, watch, formState: { errors }, getValues, setValue } = useForm<DocumentCitationState>({
        defaultValues: {
            userLabel: entry.userLabel,
            source: entry.source ?? '',
            citation: entry.citation,
            groupId: entry.groupId,
        },
    });

    const editorContents = useRef<Value>((entry.content ?? createSlateContents(entry.citation ?? '')).children as Value); // Fallback to citation due to back-compatibility

    const editor = useInlineEditor({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        initialValue: editorContents.current,
        addTrailingBlock: true
    });

    const updateEntry = useCallback(() => {
        const formValues = getValues();

        onUpdate(entryKey, {
            userLabel: formValues.userLabel ?? '',
            citation: formValues.citation,
            source: formValues.source ?? '',
            content: { children: editorContents.current },
            groupId: formValues.groupId,
        });
    }, [ editorContents, onUpdate, entryKey, getValues ]);

    useEffect(() => {
        const { unsubscribe } = watch(() => {
            updateEntry();
        });
        return () => unsubscribe();
    }, [ watch, updateEntry ]);

    useEffect(() => {
        if (entry.citation) {
            setValue('citation', null);
        }
    }, [ entry.citation ]);

    const handleEditorValueChange = useCallback((value: Value) => {
        editorContents.current = value;
        updateEntry();
    }, [ editorContents ]);

    const handleRequestHelp = useCallback(() => {
        docEditor.requestHelp({
            'Název citace': entry.userLabel || '(neuvedeno)',
            'Zdroj': entry.source || '(neuvedeno)',
            'Znění citace': entry.citation || '(neuvedeno)'
        });
    }, [ entry ]);

    const handleGenerateWithAi = useCallback(() => {
        const formValues = getValues();

        const { canUse, requiredCredits, currentBalance } = canUseFeature(AiFeatureType.CITATION_GENERATION, 1);

        if (!canUse) {
            modals.showModal('insufficient-credits', InsufficientCreditsModal, {
                currentBalance,
                requiredCredits,
                featureName: 'Generování citací',
            });
            return;
        }

        modals.showModal('credit-confirm', CreditConfirmationModal, {
            creditCost: requiredCredits,
            featureName: 'Generování citací',
            onConfirm: () => {
                useAiFeature(() => {
                    modals.showModal('ai-citation', AiCitationModal, {
                        document: docEditor.document,
                        sourceDescription: '',
                        onApply: applyAiResult
                    });
                });
            }
        });
    }, [ modals, getValues, docEditor.document, canUseFeature ]);

    const applyAiResult = useCallback((result: string, intext?: string) => {
        const newValue = editor.getApi(MarkdownPlugin).markdown.deserialize(result);
        editor.tf.setValue(newValue);
        if (intext) {
            setValue('userLabel', intext, { shouldDirty: true, shouldTouch: true });
        }
    }, [ setValue ]);

    return (
        <div className='flex flex-col items-start gap-6 px-5 py-5 border-t bg-white border-gray-200'>
            <div>
                <Label htmlFor="userLabel" className="block mb-1">Citace v textu</Label>
                <TextInput
                    id="userLabel"
                    type="text"
                    sizing='md'
                    placeholder={getUserLabelPlaceholder(docEditor.template.config.citationStyle)}
                    {...register('userLabel', {
                        required: 'Název je povinný',
                        maxLength: { value: 100, message: 'Max. délka je 100 znaků.' }
                    })}
                    readOnly={readOnly}
                    color={errors.userLabel ? 'failure' : 'default'}
                    helperText={(
                        <>
                            <span className='block text-sm text-gray-600'>Tento název slouží pro Vaše označení v dokumentu.</span>
                            {errors.userLabel && (
                                <p>
                                    {errors.userLabel.message}
                                </p>
                            )}
                        </>
                    )}
                />
            </div>

            {citationGroups.length > 0 && (
                <div className='w-full'>
                    <Label htmlFor="groupId" className="block mb-1">Kategória</Label>
                    <select
                        id="groupId"
                        className='w-full rounded-lg border border-gray-300 bg-gray-50 text-sm p-2.5 text-gray-900 focus:border-primary-500 focus:ring-primary-500'
                        disabled={readOnly}
                        {...register('groupId')}
                    >
                        <option value=''>— Bez kategorie —</option>
                        {citationGroups.map((g) => (
                            <optgroup key={g.id} label={g.label}>
                                {(g.subgroups ?? []).length > 0 ? (
                                    (g.subgroups ?? []).map((sub) => (
                                        <option key={sub.id} value={sub.id}>{sub.label}</option>
                                    ))
                                ) : (
                                    <option value={g.id}>{g.label}</option>
                                )}
                            </optgroup>
                        ))}
                    </select>
                </div>
            )}

            {!readOnly && (
                <div className='w-full flex flex-col sm:flex-row items-center justify-center gap-2'>
                    <Button
                        color='help'
                        className='flex-shrink-0'
                        onClick={handleRequestHelp}
                    ><LifeBuoyIcon size={18} /> <span className='ml-2'>Potřebujete pomoci?</span></Button>
                    <Button
                        color='primary'
                        className='flex-shrink-0'
                        onClick={handleGenerateWithAi}
                    ><SparklesIcon size={18} /> <span className='ml-2'>Zkusit doplnit citaci pomocí AI</span></Button>
                </div>
            )}

            <div className='w-full'>
                <Label htmlFor="citation" className="block mb-1">Znění citace podle normy <span className='font-semibold'>{DocumentCitationStyleLabels[docEditor.template.config.citationStyle] ?? docEditor.template.config.citationStyle}</span></Label>
                <InlineEditor
                    editor={editor}
                    readOnly={readOnly}
                    onValueChange={handleEditorValueChange}
                    className='flex flex-col gap-0 overflow-hidden border border-gray-300 rounded-lg p-1'
                    containerClass='p-2 pt-3 flex-grow flex-shrink overflow-auto'
                    toolbarClass='bg-gray-100 p-1 w-full rounded-md overflow-y-hidden overflow-x-auto flex-shrink-0'
                />
            </div>
        </div>
    );
};
